import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Auth Components
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import RoleSelect from './pages/RoleSelect';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherClassroom from './pages/TeacherClassroom';
import TeacherFeedback from './pages/TeacherFeedback';
import DuelManager from './pages/DuelManager';
import DuelArena from './pages/DuelArena';
import AdminGifs from './pages/AdminGifs';
import AdminPowers from './pages/AdminPowers';
import AdminBehaviors from './pages/AdminBehaviors';
import AdminUsers from './pages/AdminUsers';
import AdminPanel from './pages/AdminPanel';
import StudentDashboard from './pages/StudentDashboard';
import JoinClassroom from './pages/JoinClassroom';
import EggView from './pages/EggView';
import StudentTasks from './pages/StudentTasks';
import StudentMissions from './pages/StudentMissions';
import StudentPowers from './pages/StudentPowers';
import EvolutionHistory from './pages/EvolutionHistory';
import PokeShop from './pages/PokeShop';
import LabResearch from './pages/LabResearch';
import TeacherJourneys from './pages/TeacherJourneys';
import StudentJourneys, { StudentJourneyPlay } from './pages/StudentJourneys';
import StudentPokedex from './pages/StudentPokedex';

// Map-style scene transition for student routes
import IrisTransition from './components/IrisTransition';
import StudentLayout from './components/StudentLayout';

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (synchronous check during render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <IrisTransition scope="/student" />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/role-select" element={<RoleSelect />} />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      
      {/* Teacher Routes */}
      <Route
        path="/teacher/dashboard"
        element={
          <ProtectedRoute requiredRole="teacher">
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/classroom/:classroomId"
        element={
          <ProtectedRoute requiredRole="teacher">
            <TeacherClassroom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/classroom/:classroomId/duels"
        element={
          <ProtectedRoute requiredRole="teacher">
            <DuelManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/classroom/:classroomId/journeys"
        element={
          <ProtectedRoute requiredRole="teacher">
            <TeacherJourneys />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/admin/gifs"
        element={
          <ProtectedRoute requiredRole="teacher">
            <AdminGifs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/admin/powers"
        element={
          <ProtectedRoute requiredRole="teacher">
            <AdminPowers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/admin/behaviors"
        element={
          <ProtectedRoute requiredRole="teacher">
            <AdminBehaviors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/admin/users"
        element={
          <ProtectedRoute requiredRole="teacher">
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/feedback"
        element={
          <ProtectedRoute requiredRole="teacher">
            <TeacherFeedback />
          </ProtectedRoute>
        }
      />
      
      {/* Student Routes */}
      <Route
        path="/student/dashboard"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <StudentDashboard />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/duel/:lobbyId"
        element={
          <ProtectedRoute requiredRole="student">
            <DuelArena />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/join"
        element={
          <ProtectedRoute requiredRole="student">
            <JoinClassroom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/egg"
        element={
          <ProtectedRoute requiredRole="student">
            <EggView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/tasks"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <StudentTasks />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/journeys"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentJourneys />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/journeys/:journeyId"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentJourneyPlay />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/pokedex"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentPokedex />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/missions"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <StudentMissions />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/powers"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <StudentPowers />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/history"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <EvolutionHistory />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/shop"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <PokeShop />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/lab"
        element={
          <ProtectedRoute requiredRole="student">
            <StudentLayout>
              <LabResearch />
            </StudentLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
